<script lang="ts" setup>
import { microApps } from '@/micro/microApps'
import { handleActivePath } from '@/utils/routes'
import MicroApp from '@micro-zoe/micro-app'

defineOptions({
  name: 'MicroAppIndex',
})

/** useRoute */
const route = useRoute()
/** useRouter */
const router = useRouter()
/** useRoutesStore */
const routesStore = useRoutesStore()
/** useUserStore */
const userStore = useUserStore()
/** useTabsStore */
const tabsStore = useTabsStore()
/** 子系统id */
const { subSysId } = userStore
/** 子系统是否加载完毕 */
const microAppLoading = ref(true)
/** 当前路由对应子应用 */
const microName = microApps.find(i => route.path.startsWith(`/${i.name}`))?.name || ''
/** 当前路由跳转子应用地址 */
const microPath = route.fullPath.replaceAll(`/${microName}`, '')
/** 全局设置当前激活的子应用名称 */
routesStore.setMicroSubAppsName(microName)
const $pub: any = inject('$pub')

let fullPath: any

const {
  delVisitedRoute,
  delOthersVisitedRoutes,
  delLeftVisitedRoutes,
  delRightVisitedRoutes,
  delAllVisitedRoutes,
} = tabsStore
const { getVisitedRoutes: visitedRoutes } = storeToRefs(tabsStore)

const { setTitle } = useTabs()

/** 当前路由是否选中 */
const isActive = (path: string) => path === handleActivePath(route, true)

/** 当前子应用数据 */
const currentAppInfo = computed(() => {
  return microApps.find(i => i.name === routesStore.microSubAppsName)
})

/** app元素被创建 */
function handleCreated() {}
/** app即将被渲染 */
function handleBeforemount() {}
/** app已经渲染完成 */
function handleMounted() {
  // console.log(microName, microPath, '-------------------------')
  MicroApp.router.push({
    name: microName,
    path: microPath,
    replace: true,
  })
}
/** app已经卸载 */
function handleUnmount() {
  // 移除所有监听事件
  window[`eventCenterFor${currentAppInfo.value?.name}`].clearDataListener()
}
/** app渲染出错 */
function handleError() {
  console.log('%c[micro-app渲染出错]', 'color:#cf1306')
}
/** 子应用数据变动回调 */
function handleDataChange(e: CustomEvent): void {
  console.log('[子应用数据变动]:', e.detail.data)
  const data = e.detail.data
  if (data?.isMicroAppDone) {
    microAppLoading.value = false
    console.log('microAppLoading')
    // aftershow()
  }
}
/** 已经推入后台 */
function afterhidden() {
  console.log('%c已经推入后台', 'color:#ada582')
}
/** 即将推入前台，初始化时不执行 */
function beforeshow() {}
/** c已经推入前台，初始化时不执行 */
function aftershow() {
  console.log('aftershow')
  MicroApp.router.push({
    name: microName,
    path: microPath,
    replace: true,
  })

  microAppLoading.value = false
}
/** 修改页面和tab标题 */
function handleSetTitle(title: string) {
  setTitle(title, fullPath)
}
/** 删除单个标签事件 */
async function handleDelVisitedRoute(rawPath: string, isMicroDel = true) {
  // 看是否path在tab中，如果不存在，再判断是否在泛匹配路由中
  let delTarget: any = visitedRoutes.value.find(route => route.path === rawPath)
  if (!delTarget) {
    // 如果没找到，就把微应用的泛匹配路由tab干掉  兼容子应用自主关闭tab 而主应用不关闭tab的问题
    const name = microApps.find(i => rawPath.startsWith(`/${i.name}`))?.name
    delTarget = visitedRoutes.value.find(route => route.path === `/${name}/:page*`)
  }
  console.log(isActive(delTarget.path))
  delVisitedRoute(delTarget.path, isMicroDel)
  if (isActive(delTarget.path))
    await toLastTab()
}
/** 删除其他标签事件 */
async function handleDelOthersVisitedRoutes(rawPath: string, isMicroDel = true) {
  delOthersVisitedRoutes(rawPath, isMicroDel)
  if (!isActive(rawPath))
    await toLastTab()
}
/** 删除左边全部标签事件 */
async function handleDelLeftVisitedRoutes(rawPath: string, isMicroDel = true) {
  delLeftVisitedRoutes(rawPath, isMicroDel)
  if (!isActive(rawPath))
    await toLastTab()
}
/** 删除右边全部标签事件 */
async function handleDelRightVisitedRoutes(rawPath: string, isMicroDel = true) {
  delRightVisitedRoutes(rawPath, isMicroDel)
  if (!isActive(rawPath))
    await toLastTab()
}
/** 删除全部标签事件 */
async function handleDelAllVisitedRoutes(noClosePaths?: string[], isMicroDel = true) {
  delAllVisitedRoutes(noClosePaths, isMicroDel)
  await toLastTab()
}
/** 跳转最后一个标签页 */
async function toLastTab() {
  const latestView = visitedRoutes.value
    .filter((_: any) => _.path !== handleActivePath(route, true))
    .slice(-1)[0]
  if (latestView)
    await router.push(latestView)
  else await router.push('/')
}
/** 刷新当前标签页 */
function refreshTab() {
  $pub('reload-router-view')
}

onMounted(() => {
  fullPath = { ...route }
})
</script>

<template>
  <div v-loading="microAppLoading" class="micro-app-wrap">
    <!-- eslint-disable-next-line vue/component-name-in-template-casing -->
    <micro-app
      v-bind="currentAppInfo"
      :data="{
        systemId: subSysId,
        systemKey: 'CRM',
        ...(currentAppInfo?.data || {}),
        mainAppRouter: router,
        tabsStore,
        setTitle: handleSetTitle,
        delVisitedRoute: handleDelVisitedRoute,
        delOthersVisitedRoutes: handleDelOthersVisitedRoutes,
        delLeftVisitedRoutes: handleDelLeftVisitedRoutes,
        delRightVisitedRoutes: handleDelRightVisitedRoutes,
        delAllVisitedRoutes: handleDelAllVisitedRoutes,
        refreshTab,
      }"
      class="h-100%"
      router-mode="state"
      :default-page="microPath"
      @datachange="handleDataChange"
      @created="handleCreated"
      @beforemount="handleBeforemount"
      @mounted="handleMounted"
      @unmount="handleUnmount"
      @error="handleError"
      @afterhidden="afterhidden"
      @beforeshow="beforeshow"
      @aftershow="aftershow"
    />
  </div>
</template>

<style lang="scss" scoped>
.micro-app-wrap {
  width: 100%;
  height: 100%;

  :deep() {
    micro-app,
    micro-app-body {
      height: 100%;
    }
  }
}
</style>
